import cx from "classnames";
import { useMemo, useState } from "react";
import { t } from "ttag";

import { EmptyState } from "metabase/actions/containers/ActionPicker/ActionPicker.styled";
import type { GlossaryItem } from "metabase/api";
import { ConfirmModal } from "metabase/common/components/ConfirmModal";
import { Table as CommonTable } from "metabase/common/components/Table/Table";
import { NoObjectError } from "metabase/common/components/errors/NoObjectError";
import { useSetting } from "metabase/settings";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Icon,
  Text,
  Tooltip,
} from "metabase/ui";
import type { SortDirection } from "metabase-types/api";

import S from "./Glossary.module.css";
import { GlossaryRowEditor } from "./GlossaryRowEditor";
import type { GlossaryField } from "./types";

export type GlossaryTableProps = {
  className?: string;
  glossary: GlossaryItem[];
  readOnly?: boolean;
  onCreate?: (term: string, definition: string) => Promise<void> | void;
  onEdit?: (
    id: number,
    term: string,
    definition: string,
  ) => Promise<void> | void;
  onDelete?: (id: number) => Promise<void> | void;
};

export function GlossaryTable({
  className,
  glossary,
  readOnly = false,
  onCreate,
  onEdit,
  onDelete,
}: GlossaryTableProps) {
  const [isCreating, setIsCreating] = useState(false);
  const metabotName = useSetting("metabot-name");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<GlossaryField | null>(null);
  const [deletingItem, setDeletingItem] = useState<GlossaryItem | null>(null);
  const [sortColumnName, setSortColumnName] = useState<
    keyof GlossaryItem | null
  >(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const startEditing = (id: number, field: GlossaryField) => {
    if (readOnly) {
      return;
    }
    if (isCreating) {
      setIsCreating(false);
    }
    setEditingId(id);
    setEditingField(field);
  };

  const sortedRows = useMemo(() => {
    if (!sortColumnName) {
      return [
        ...(isCreating ? [{ id: -1, kind: "create" as const }] : []),
        ...glossary,
      ];
    }
    const data = glossary.toSorted((a, b) => {
      const ak = a[sortColumnName] ?? "";
      const bk = b[sortColumnName] ?? "";
      const cmp = String(ak).localeCompare(String(bk));
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return [
      ...(isCreating ? [{ id: -1, kind: "create" as const }] : []),
      ...data,
    ];
  }, [isCreating, glossary, sortColumnName, sortDirection]);

  return (
    <>
      <Group justify="space-between" mb="xs">
        <Text>
          {t`Define terms to help your team and ${metabotName} understand your data.`}
        </Text>
        {!readOnly && (
          <Button
            variant="default"
            size="sm"
            leftSection={<Icon name="add" />}
            onClick={() => {
              if (editingId) {
                setEditingId(null);
                setEditingField(null);
              }
              setIsCreating(true);
            }}
          >
            {t`New term`}
          </Button>
        )}
      </Group>
      <CommonTable
        className={cx(S.table, className)}
        columns={[
          { name: t`Term`, key: "term", sortable: true },
          { name: t`Definition`, key: "definition", sortable: true },
          { name: "", key: "actions", sortable: false },
        ]}
        rows={sortedRows}
        cols={
          <>
            <col style={{ width: "22.5%" }} />
            <col style={{ width: "65%" }} />
            <col style={{ width: "5rem" }} />
          </>
        }
        emptyBody={
          <Center>
            <EmptyState
              message={t`No terms yet`}
              illustrationElement={<NoObjectError mb="-1.5rem" />}
            />
          </Center>
        }
        sortColumnName={sortColumnName}
        sortDirection={sortDirection}
        onSort={(column, dir) => {
          // Unjustified type cast. FIXME
          setSortColumnName(column as keyof GlossaryItem);
          setSortDirection(dir);
        }}
        rowRenderer={(row) => {
          // Create row
          if ("kind" in row && row.kind === "create") {
            const existingTerms = glossary.map((g) => g.term);
            return (
              <tr className={cx(S.row, S.rowEditor)}>
                <GlossaryRowEditor
                  item={{ term: "", definition: "" }}
                  existingTerms={existingTerms}
                  onCancel={() => setIsCreating(false)}
                  onSave={async (term, definition) => {
                    await onCreate?.(term, definition);
                    setIsCreating(false);
                  }}
                />
              </tr>
            );
          }

          // Unjustified type cast. FIXME
          const item = row as GlossaryItem;
          const isEditing = editingId === item.id;

          return (
            <tr
              className={cx(S.row, {
                [S.rowEditor]: isEditing,
                [S.readOnly]: readOnly,
              })}
            >
              {isEditing ? (
                <GlossaryRowEditor
                  item={item}
                  autoFocusField={editingField ?? "term"}
                  existingTerms={glossary
                    .filter((g) => g.id !== item.id)
                    .map((g) => g.term)}
                  onCancel={() => setEditingId(null)}
                  onSave={async (newTerm, newDefinition) => {
                    await onEdit?.(item.id, newTerm, newDefinition);
                    setEditingId(null);
                    setEditingField(null);
                  }}
                />
              ) : (
                <>
                  <Box
                    component="td"
                    valign="top"
                    onClick={() => startEditing(item.id, "term")}
                  >
                    <Text lh="1.2" fw="bold" pt="xs">
                      {item.term}
                    </Text>
                  </Box>
                  <Box
                    component="td"
                    valign="top"
                    style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                    onClick={() => startEditing(item.id, "definition")}
                  >
                    <Text lh="1.2" pt="xs">
                      {item.definition}
                    </Text>
                  </Box>

                  <Box
                    component="td"
                    valign="top"
                    align="center"
                    p="sm"
                    w="25px"
                  >
                    {!readOnly && (
                      <Tooltip label={t`Delete`}>
                        <ActionIcon
                          aria-label={t`Delete`}
                          variant="subtle"
                          c="text-disabled"
                          className={cx(S.action)}
                          onClick={() => setDeletingItem(item)}
                        >
                          <Icon name="trash" />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Box>
                </>
              )}
            </tr>
          );
        }}
      />
      <ConfirmModal
        opened={deletingItem != null}
        title={t`Delete “${deletingItem?.term}”?`}
        confirmButtonText={t`Delete`}
        message={t`This can't be undone.`}
        onClose={() => setDeletingItem(null)}
        onConfirm={async () => {
          if (deletingItem) {
            await onDelete?.(deletingItem.id);
            setDeletingItem(null);
          }
        }}
      />
    </>
  );
}
